import { GenderType, InsuranceKey } from 'amuvee-shared';

// Raw breakdown kept alongside the aggregated `income` total, for form rehydration.
export interface IncomeDetails {
  gross?: number | null;
  disabilityPensionExists?: boolean | null;
  disabilityPension?: number | null;
  entertainsExists?: boolean | null;
  entertains?: number | null;
  childBenefitExists?: boolean | null;
  childBenefit?: number | null;
  rentalIncomeExists?: boolean | null;
  rentalIncome?: number | null;
}

/**
 * A saved profile. `null` means "answered and then cleared" and is the ONLY
 * value that erases a stored answer — UserActivityService.updateUserHistory
 * merges each save onto the previous entry and skips undefined/''/[], so a
 * cleared field sent as undefined silently keeps its old value.
 */
export interface UserInfo {
  childrenAgeGroups?: boolean[] | null;
  childrenCount?: number | null;
  filterByDate?: boolean;
  income?: number | null;
  incomeDetails?: IncomeDetails;
  insurance?: InsuranceKey | null;
  isPregnant?: boolean | null;
  isRefugee?: boolean | null;
  isVictimOfViolence?: boolean | null;
  jobRelatedSituation?: number | null;
  parentAge?: number | null;
  parentGender?: GenderType | null;
  relationship?: number | null;
  rent?: number | null;
  status?: string;
  zip?: string | null;
}

// Angular's HttpClient serializes an explicit `undefined` property as the
// literal string "undefined" in the query string, which the API's truthy
// checks (`if (input.x) ...`) then treat as a real answered value. Omitting
// the key entirely — not just setting it to undefined — is required.
export function omitUndefined<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
}

/* Amount controls are hidden, never reset, when their "…vorhanden?" answer
   stops being yes — so a raw form value still carries figures the user has
   since answered no to. */
export function clearInactiveIncomeAmounts(
  details: IncomeDetails,
): IncomeDetails {
  return {
    ...details,
    disabilityPension: details.disabilityPensionExists
      ? details.disabilityPension
      : null,
    entertains: details.entertainsExists ? details.entertains : null,
    childBenefit: details.childBenefitExists ? details.childBenefit : null,
    rentalIncome: details.rentalIncomeExists ? details.rentalIncome : null,
  };
}

// Single source of truth for gross + enabled supplemental income sources.
export function aggregateGrossIncome(
  income: IncomeDetails | null | undefined,
): number | null {
  if (income?.gross == null || isNaN(income.gross)) {
    return null;
  }
  let total = income.gross;
  if (income.disabilityPensionExists && income.disabilityPension != null) {
    total += income.disabilityPension;
  }
  if (income.entertainsExists && income.entertains != null) {
    total += income.entertains;
  }
  if (income.childBenefitExists && income.childBenefit != null) {
    total += income.childBenefit;
  }
  if (income.rentalIncomeExists && income.rentalIncome != null) {
    total += income.rentalIncome;
  }
  return total;
}

export function UserInfoKeyToString(
  userInfo: UserInfo,
  key: keyof UserInfo,
): string {
  //test if key is in userInfo
  if (key in userInfo) {
    //test if value is null
    if (userInfo[key] === null || userInfo[key] === undefined)
      return 'nicht angegeben';
    //test if value is boolean
    if (typeof userInfo[key] === 'boolean') {
      return userInfo[key] ? 'Ja' : 'Nein';
    }
    if (key === 'parentGender') {
      return formatGender(userInfo[key]);
    }

    if (key === 'parentAge') {
      const age = userInfo[key];
      if (age) {
        return age + ' Jahre';
      } else {
        return 'nicht angegeben';
      }
    }
    if (key === 'relationship') {
      return formatRelationship(userInfo[key]);
    }
    if (key === 'childrenAgeGroups') {
      const ageGroups = userInfo[key];
      if (ageGroups && ageGroups.length > 0) {
        return formatChildAge(ageGroups);
      } else {
        return 'nicht angegeben';
      }
    }
    if (key === 'insurance' && userInfo[key] != undefined) {
      return formatInsurance(userInfo[key]);
    }
    if (key === 'jobRelatedSituation') {
      return jobSituation(userInfo[key]);
    }
    //test if value is string
    if (typeof userInfo[key] === 'string') {
      return userInfo[key];
    }
    //test if value is number
    if (typeof userInfo[key] === 'number') {
      if (key === 'rent' || key === 'income') {
        return userInfo[key] + ' € pro Monat';
      }
      return userInfo[key].toString();
    }
  }
  return '';
}

function formatInsurance(insurance: InsuranceKey): string {
  switch (insurance) {
    case 'PUB_AOK':
      return 'AOK';
    case 'PUB_TK':
      return 'Techniker Krankenkasse (TK)';
    case 'PUB_BARMER_GEK':
      return 'Barmer GEK';
    case 'PUB_DAK':
      return 'DAK';
    case 'PRIV_DEBEKA':
      return 'debeka';
    case 'PRIV_DKV':
      return 'DKV (Viktoria)';
    case 'PRIV_ALLIANZ':
      return 'Allianz private Krankenversicherung';
    case 'PRIV_AXA':
      return 'AXA (Colonia, DBV Winterthur)';
    case 'PUB_OTHER':
    case 'PRIV_OTHER':
      return 'Sonstige';
  }
}

function formatGender(gender: string): string {
  if (!gender) return '';

  switch (gender.toUpperCase()) {
    case 'MALE':
      return 'Männlich';
    case 'FEMALE':
      return 'Weiblich';
    case 'OTHER':
      return 'Divers';
    default:
      return gender;
  }
}

function formatChildAge(selection: boolean[]): string {
  let ageGroups: string[] = [
    '0 - 5 Monate',
    '6 - 11 Monate',
    '12 - 17 Monate',
    '18 - 23 Monate',
    '2 Jahren',
    '3 Jahre',
    '4 Jahre',
    '5 Jahre',
    '6 Jahre',
    '7 Jahre',
    '8 Jahre',
    '9 Jahre',
    '10 Jahrn',
    '11 Jahre',
    '12 Jahre',
    '13 Jahre',
    '14 Jahre',
    '15 Jahre',
    '16 Jahre',
    '17 Jahre',
    '18 Jahren oder älter',
  ];
  if (ageGroups.length === selection.length) {
    const input = ageGroups.filter((_, index) => selection[index]);
    if (input.length === 0) return 'nicht angegeben';
    if (input.length === 1) return input[0];
    if (input.length === 2) return input[0] + ' und ' + input[1];
    const last = input.pop();
    return input.join(', ') + ' und ' + last;
  }
  return '';
}

/**
 * Single shared mapping — was previously duplicated (and subtly wrong in
 * every copy) across request.service.ts, search-engine.service.ts, and
 * account-children-dialog.component.ts. numberOfChildren's form control is
 * only ever hidden, never reset, when alreadyChildren flips back to false —
 * so a stale "1" from an earlier "Yes" answer lingers after switching to
 * "No". Checking alreadyChildren first, instead of falling back to it only
 * when numberOfChildren is falsy/null, is what makes an explicit "No" win
 * regardless of what's left over in that hidden field.
 */
export function childrenCountFromChildrenAnswers(values: {
  alreadyChildren?: boolean | null;
  numberOfChildren?: number | null;
}): number | null {
  if (values.alreadyChildren === false) return 0;
  if (values.alreadyChildren === true) return values.numberOfChildren ?? null;
  return null;
}

// Single shared mapping — was previously duplicated and drifted across
// request.service.ts and account-life-situation-dialog.component.ts.
export function relationshipCodeFromLifeSituation(values: {
  lifeSituationBasic?: number | null;
  lifeSituationDivorce?: number | null;
}): number | null {
  if (values.lifeSituationBasic == 0) return 1;
  if (values.lifeSituationBasic == 2) return 5;
  if (values.lifeSituationBasic == 3) return 6;
  if (values.lifeSituationBasic == 1 && values.lifeSituationDivorce == 0)
    return 2;
  if (values.lifeSituationBasic == 1 && values.lifeSituationDivorce == 1)
    return 3;
  if (values.lifeSituationBasic == 1 && values.lifeSituationDivorce == 2)
    return 4;
  return null;
}

/**
 * Single shared mapping from the Arbeitssituation answers to the stored 1-14
 * code. SearchEngineService.saveSearchData used to persist the raw `situation`
 * (0-4) here instead, which the dashboard then read back as an unrelated code.
 */
export function jobRelatedSituationFromWorkAnswers(values: {
  situation?: number | null;
  jobType?: number | null;
  partTimeType?: number | null;
  education?: number | null;
  socialBenefitType?: number | null;
}): number | null {
  const { situation, jobType, partTimeType, education, socialBenefitType } =
    values;
  if (situation == 0 && jobType == 0) return 1;
  if (situation == 0 && jobType == 1 && partTimeType == 0) return 2;
  if (situation == 0 && jobType == 1 && partTimeType == 1) return 3;
  if (situation == 0 && jobType == 2) return 4;
  if (situation == 0 && jobType == 3) return 5;
  if (situation == 1 && education == 0) return 6;
  if (situation == 1 && education == 1) return 7;
  if (situation == 1 && education == 2) return 8;
  if (situation == 2 && socialBenefitType == 0) return 9;
  if (situation == 2 && socialBenefitType == 1) return 10;
  if (situation == 2 && socialBenefitType == 2) return 11;
  if (situation == 2 && socialBenefitType == 3) return 12;
  if (situation == 3) return 13;
  if (situation == 4) return 14;
  return null;
}

export function lifeSituationFromRelationshipCode(
  relationship: number | null | undefined,
): { lifeSituationBasic: number | null; lifeSituationDivorce: number | null } {
  switch (relationship) {
    case 1:
      return { lifeSituationBasic: 0, lifeSituationDivorce: null };
    case 2:
      return { lifeSituationBasic: 1, lifeSituationDivorce: 0 };
    case 3:
      return { lifeSituationBasic: 1, lifeSituationDivorce: 1 };
    case 4:
      return { lifeSituationBasic: 1, lifeSituationDivorce: 2 };
    case 5:
      return { lifeSituationBasic: 2, lifeSituationDivorce: null };
    case 6:
      return { lifeSituationBasic: 3, lifeSituationDivorce: null };
    default:
      return { lifeSituationBasic: null, lifeSituationDivorce: null };
  }
}

export interface WorkSituationValue {
  situation?: number | null;
  jobType?: number | null;
  partTimeType?: number | null;
  education?: number | null;
  socialBenefitType?: number | null;
}

/**
 * Single shared mapping — was previously duplicated (and, in
 * search-engine.service.ts, missing entirely — the raw `situation` value
 * was persisted instead) across request.service.ts and
 * account-work-dialog.component.ts.
 */
export function jobRelatedSituationCodeFromWorkSituation(
  value: WorkSituationValue,
): number | null {
  if (value.situation === 0 && value.jobType === 0) return 1;
  if (value.situation === 0 && value.jobType === 1 && value.partTimeType === 0)
    return 2;
  if (value.situation === 0 && value.jobType === 1 && value.partTimeType === 1)
    return 3;
  if (value.situation === 0 && value.jobType === 2) return 4;
  if (value.situation === 0 && value.jobType === 3) return 5;
  if (value.situation === 1 && value.education === 0) return 6;
  if (value.situation === 1 && value.education === 1) return 7;
  if (value.situation === 1 && value.education === 2) return 8;
  if (value.situation === 2 && value.socialBenefitType === 0) return 9;
  if (value.situation === 2 && value.socialBenefitType === 1) return 10;
  if (value.situation === 2 && value.socialBenefitType === 2) return 11;
  if (value.situation === 2 && value.socialBenefitType === 3) return 12;
  if (value.situation === 3) return 13;
  if (value.situation === 4) return 14;
  return null;
}

export function workSituationFromJobRelatedSituationCode(
  code: number | null | undefined,
): WorkSituationValue {
  switch (code) {
    case 1:
      return { situation: 0, jobType: 0 };
    case 2:
      return { situation: 0, jobType: 1, partTimeType: 0 };
    case 3:
      return { situation: 0, jobType: 1, partTimeType: 1 };
    case 4:
      return { situation: 0, jobType: 2 };
    case 5:
      return { situation: 0, jobType: 3 };
    case 6:
      return { situation: 1, education: 0 };
    case 7:
      return { situation: 1, education: 1 };
    case 8:
      return { situation: 1, education: 2 };
    case 9:
      return { situation: 2, socialBenefitType: 0 };
    case 10:
      return { situation: 2, socialBenefitType: 1 };
    case 11:
      return { situation: 2, socialBenefitType: 2 };
    case 12:
      return { situation: 2, socialBenefitType: 3 };
    case 13:
      return { situation: 3 };
    case 14:
      return { situation: 4 };
    default:
      return {};
  }
}

function formatRelationship(relationship: number): string {
  switch (relationship) {
    case 1:
      return 'Alleinstehend';
    case 2:
      return 'In Trennung: Verheiratet';
    case 3:
      return 'In Trennung: Verpartnert';
    case 4:
      return 'In Trennung: Liiert';
    case 5:
      return 'Geschieden';
    case 6:
      return 'Verwitwet';
  }
  return 'nicht gegeben ' + relationship;
}

function jobSituation(jobRelatedSituation: number): string {
  switch (jobRelatedSituation) {
    case 1:
      return 'Berufstätig: Vollzeit';
    case 2:
      return 'Berufstätig: Teilzeit unter 30 Stunden';
    case 3:
      return 'Berufstätig: Teilzeit 30 oder mehr Stunden';
    case 4:
      return 'Berufstätig: Geringfügig (538 EUR)';
    case 5:
      return 'Berufstätig: Selbstständig';
    case 6:
      return 'In Ausbildung: (Duales) Studium';
    case 7:
      return 'In Ausbildung: Ausbildung / Lehre';
    case 8:
      return 'In Ausbildung: Freiwilliges Soziales Jahr';
    case 9:
      return 'Sozialleistungen: Sozialgeld';
    case 10:
      return 'Sozialleistungen: Sozialhilfe';
    case 11:
      return 'Sozialleistungen: Bürgergeld';
    case 12:
      return 'Sozialleistungen: Bürgergeld-Aufstocker';
    case 13:
      return 'Arbeitslosigkeit';
    case 14:
      return 'Ohne Tätigkeit';
  }
  return 'nicht gegeben';
}
